import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Cookie, Shield, Settings, BarChart3, Target, Clock, ToggleLeft, ToggleRight, Info, Mail } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './CookiePolicy.css';

const CookiePolicy = () => {
  const lastUpdated = "January 20, 2026";
  
  const [cookiePreferences, setCookiePreferences] = useState({
    essential: true,
    analytics: false,
    functional: false,
  });

  const togglePreference = (key) => {
    if (key === 'essential') return; // Essential cookies cannot be disabled
    setCookiePreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const sections = [
    {
      id: 'what-are-cookies',
      icon: Cookie,
      title: 'What Are Cookies?',
      content: `Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit a website. They are widely used to make websites work more efficiently and provide information to website owners.

**How Cookies Work:**
• When you visit Web3 Ledger, our server sends a cookie to your browser
• Your browser stores the cookie on your device
• On subsequent visits, the cookie is sent back to our server
• This helps us recognize you and remember your preferences

**Types of Storage We Use:**
• **Cookies** - Small text files stored by your browser
• **Local Storage** - Data stored locally in your browser
• **Session Storage** - Temporary data cleared when you close your browser

Cookies do not contain personal information like your name or email address, and they cannot be used to identify you personally unless you have provided that information to us.`
    },
    {
      id: 'cookies-we-use',
      icon: Settings,
      title: 'Cookies We Use',
      content: `Web3 Ledger uses a minimal set of cookies to provide and improve our services:

**Essential Cookies (Required)**
These cookies are necessary for the Platform to function properly:
• **Session cookies** - Maintain your session while using the Platform
• **Security tokens** - Help protect against security threats
• **Wallet connection** - Remember your connected wallet state
• **Language preference** - Store your preferred language setting

**Analytics Cookies (Optional)**
Help us understand how visitors use our Platform:
• **Usage patterns** - Pages visited and features used
• **Performance data** - Load times and error tracking
• **Aggregated statistics** - Non-identifying usage trends

**Functional Cookies (Optional)**
Enhance your experience with additional features:
• **Theme preference** - Remember dark/light mode setting
• **Dashboard layout** - Save your customized layouts
• **Notification settings** - Store your alert preferences`
    },
    {
      id: 'cookie-purposes',
      icon: Target,
      title: 'How We Use Cookies',
      content: `We use cookies for the following purposes:

**Platform Operation:**
• Ensuring the Platform functions correctly
• Maintaining your session security
• Processing your transactions safely
• Remembering your wallet connections

**Security:**
• Detecting and preventing fraud
• Protecting against unauthorized access
• Monitoring for suspicious activity
• Validating user sessions

**Performance Improvement:**
• Identifying and fixing bugs
• Optimizing load times
• Improving user experience
• Testing new features

**Personalization:**
• Remembering your preferences
• Customizing your dashboard
• Storing your settings
• Providing relevant content

**We Do NOT Use Cookies For:**
• Advertising or ad targeting
• Tracking you across other websites
• Selling data to third parties
• Building marketing profiles`
    },
    {
      id: 'third-party',
      icon: Shield,
      title: 'Third-Party Cookies',
      content: `We minimize the use of third-party cookies. However, some may be set by:

**Blockchain Networks:**
When you connect to decentralized applications or blockchain networks, those services may set their own cookies. These are governed by their respective privacy policies.

**Security Services:**
We use security services to protect against attacks. These services may use cookies to:
• Verify you are human (CAPTCHA)
• Detect malicious activity
• Protect against DDoS attacks

**Analytics Providers:**
If you opt-in to analytics, we may use privacy-focused analytics that:
• Do not track you across websites
• Do not sell your data
• Aggregate data anonymously
• Respect Do Not Track signals

**Our Commitment:**
• We do not allow advertising cookies
• We do not use social media tracking pixels
• We regularly audit third-party cookies
• We only partner with privacy-respecting services`
    },
    {
      id: 'cookie-duration',
      icon: Clock,
      title: 'Cookie Duration',
      content: `Cookies have different lifespans depending on their purpose:

**Session Cookies:**
• Deleted when you close your browser
• Used for: Session management, security tokens
• Duration: Until browser is closed

**Persistent Cookies:**
• Remain on your device for a set period
• Used for: Preferences, settings, analytics
• Duration: Varies by purpose

**Specific Cookie Durations:**

| Cookie Type | Duration |
|-------------|----------|
| Session ID | Session only |
| Security token | 24 hours |
| Language preference | 1 year |
| Theme setting | 1 year |
| Analytics (if enabled) | 30 days |
| Wallet connection state | 7 days |

You can clear all cookies at any time through your browser settings. Note that this may require you to re-enter preferences and reconnect your wallet.`
    },
    {
      id: 'analytics',
      icon: BarChart3,
      title: 'Analytics & Tracking',
      content: `We believe in privacy-first analytics. Here's our approach:

**What We Collect (When Enabled):**
• Pages viewed and time spent
• Features used and interactions
• Device type and browser (anonymized)
• General geographic region (country level)
• Error reports and crash data

**What We DON'T Collect:**
• Personal identifying information
• Precise location data
• Browsing history on other sites
• Financial or wallet information
• IP addresses (anonymized immediately)

**Privacy-First Approach:**
• Data is aggregated, not individual
• We use privacy-focused analytics tools
• No cross-site tracking
• Respect browser Do Not Track settings
• Data retained for maximum 90 days

**Opt-Out:**
You can disable analytics cookies at any time using the preference center below or your browser settings.`
    },
    {
      id: 'your-choices',
      icon: ToggleRight,
      title: 'Your Cookie Choices',
      content: `You have control over how cookies are used on Web3 Ledger:

**Browser Settings:**
Most browsers allow you to:
• Block all cookies
• Block only third-party cookies
• Clear existing cookies
• Get notified when cookies are set
• Delete cookies when closing browser

**Popular Browser Instructions:**
• **Chrome**: Settings → Privacy and Security → Cookies
• **Firefox**: Settings → Privacy & Security → Cookies
• **Safari**: Preferences → Privacy → Cookies
• **Edge**: Settings → Cookies and Site Permissions

**Do Not Track:**
We respect the Do Not Track (DNT) browser setting. When enabled, we will not set optional cookies.

**Cookie Preference Center:**
Use the preference center below to manage your cookie settings for Web3 Ledger specifically.

**Note:** Disabling essential cookies may prevent you from using certain features of the Platform. We recommend keeping essential cookies enabled for the best experience.`
    },
    {
      id: 'updates',
      icon: Info,
      title: 'Policy Updates',
      content: `We may update this Cookie Policy from time to time to reflect:

• Changes in our cookie practices
• New features or services
• Legal or regulatory requirements
• Security improvements

**How We Notify You:**
• Update the "Last Updated" date at the top
• Post notices on the Platform for significant changes
• Email registered users about major updates

**Your Continued Use:**
Continuing to use Web3 Ledger after changes to this policy constitutes acceptance of the updated terms.

**Review Recommendations:**
We encourage you to review this Cookie Policy periodically to stay informed about our cookie practices.`
    },
    {
      id: 'contact',
      icon: Mail,
      title: 'Contact Us',
      content: `If you have questions about our use of cookies, please contact us:

**Email:** privacy@web3ledger.com

**Subject Line:** Cookie Policy Inquiry

**Response Time:** We aim to respond within 48 hours

**Additional Resources:**
• Privacy Policy - Learn more about how we protect your data
• Terms of Service - Understand your rights and obligations
• Help Center - Get answers to common questions

We're committed to transparency about our cookie practices and welcome any questions or feedback.`
    }
  ];

  const cookieTypes = [
    {
      key: 'essential',
      name: 'Essential Cookies',
      description: 'Required for the Platform to function. Cannot be disabled.',
      required: true
    },
    {
      key: 'analytics',
      name: 'Analytics Cookies',
      description: 'Help us understand how you use the Platform to improve it.',
      required: false
    },
    {
      key: 'functional',
      name: 'Functional Cookies',
      description: 'Remember your preferences and personalize your experience.',
      required: false
    }
  ];

  return (
    <div className="cookie-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="cookie-hero">
        <div className="hero-glow"></div>
        <div className="container">
          <motion.div 
            className="cookie-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="hero-badge">
              <Cookie size={14} />
              Transparency First
            </span>
            <h1>Cookie Policy</h1>
            <p>How we use cookies to improve your experience</p>
            <span className="last-updated">Last Updated: {lastUpdated}</span>
          </motion.div>
        </div>
      </section>

      {/* Quick Navigation */}
      <section className="cookie-nav-section">
        <div className="container">
          <div className="cookie-nav">
            {sections.map((section) => (
              <a 
                key={section.id} 
                href={`#${section.id}`}
                className="cookie-nav-item"
              >
                <section.icon size={16} />
                <span>{section.title}</span>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Cookie Preference Center */}
      <section className="cookie-preferences-section">
        <div className="container">
          <motion.div 
            className="preferences-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="preferences-header">
              <Settings size={24} />
              <div>
                <h2>Cookie Preference Center</h2>
                <p>Manage your cookie settings</p>
              </div>
            </div>
            <div className="preferences-list">
              {cookieTypes.map((cookie) => (
                <div key={cookie.key} className="preference-item">
                  <div className="preference-info">
                    <h3>{cookie.name}</h3>
                    <p>{cookie.description}</p>
                  </div>
                  <button 
                    className={`preference-toggle ${cookiePreferences[cookie.key] ? 'active' : ''} ${cookie.required ? 'required' : ''}`}
                    onClick={() => togglePreference(cookie.key)}
                    disabled={cookie.required}
                    aria-label={`Toggle ${cookie.name}`}
                  >
                    {cookiePreferences[cookie.key] ? (
                      <ToggleRight size={28} />
                    ) : (
                      <ToggleLeft size={28} />
                    )}
                  </button>
                </div>
              ))}
            </div>
            <div className="preferences-actions">
              <button className="btn btn-secondary" onClick={() => setCookiePreferences({ essential: true, analytics: false, functional: false })}>
                Reject Optional
              </button>
              <button className="btn btn-primary" onClick={() => setCookiePreferences({ essential: true, analytics: true, functional: true })}>
                Accept All
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content Sections */}
      <section className="cookie-content">
        <div className="container">
          <div className="cookie-grid">
            {sections.map((section, index) => (
              <motion.div
                key={section.id}
                id={section.id}
                className="cookie-section"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="section-icon">
                  <section.icon size={24} />
                </div>
                <h2>{section.title}</h2>
                <div className="section-content">
                  {section.content.split('\n\n').map((paragraph, i) => (
                    <p key={i} dangerouslySetInnerHTML={{ 
                      __html: paragraph
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\n•/g, '<br/>•')
                        .replace(/\n\|/g, '<br/>|')
                        .replace(/\n/g, '<br/>')
                    }} />
                  ))}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Summary Box */}
          <motion.div 
            className="cookie-summary"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="summary-icon">
              <Cookie size={32} />
            </div>
            <h3>Our Cookie Commitment</h3>
            <p>
              At Web3 Ledger, we use cookies responsibly. We don't use advertising 
              cookies, we don't track you across websites, and we don't sell your data. 
              Our cookies exist solely to make the Platform work better for you.
            </p>
            <div className="summary-points">
              <div className="summary-point">
                <Shield size={18} />
                <span>No ad tracking</span>
              </div>
              <div className="summary-point">
                <Target size={18} />
                <span>No cross-site tracking</span>
              </div>
              <div className="summary-point">
                <BarChart3 size={18} />
                <span>Privacy-first analytics</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CookiePolicy;
